/**
 * Central query-key factory. Never inline strings in `useQuery` — go
 * through this factory so invalidations hit the right nodes and refactors
 * are mechanical.
 *
 * Convention: nested keys start with a resource group, then the scope
 * (channel, id...). Invalidate by prefix: `queryClient.invalidateQueries({
 * queryKey: queryKeys.videos.all })`.
 */
export const queryKeys = {
  channels: {
    all: ['channels'] as const,
    list: () => [...queryKeys.channels.all, 'list'] as const,
    byId: (slug: string) => [...queryKeys.channels.all, 'one', slug] as const,
    settings: (slug: string) => [...queryKeys.channels.all, 'settings', slug] as const,
    factoryUsage: (slug: string) => [...queryKeys.channels.all, 'factory-usage', slug] as const,
  },
  videos: {
    all: ['videos'] as const,
    list: (params: {
      channel: string
      page: number
      limit: number
      sort: string
      status: string
      scope: string
      exclude: readonly string[]
      hideVisibility: readonly string[]
    }) => [...queryKeys.videos.all, 'list', params] as const,
    byId: (videoId: string, lang: string) =>
      [...queryKeys.videos.all, 'one', videoId, lang] as const,
  },
  factories: {
    all: ['factories'] as const,
    // ``channel`` carries the per-channel cost estimate aggregation
    // (decisão 55). Two distinct keys (no channel vs each channel) so
    // the global-defaults read stays cached separately from the
    // per-channel one — invalidating one doesn't refetch the other.
    catalog: (channel?: string | null) =>
      [...queryKeys.factories.all, 'catalog', channel ?? null] as const,
  },
  jobs: {
    all: ['jobs'] as const,
    list: (params: {
      category?: string | null
      status?: string | null
      limit: number
      offset: number
    }) => [...queryKeys.jobs.all, 'list', params] as const,
    /**
     * Shared feed of the user's most recent jobs. Polled across the app
     * (sidebar badge, topbar bell, jobs page, JobsWatcher) — a single key
     * so TanStack Query dedupes the polling.
     */
    feed: (limit: number) => [...queryKeys.jobs.all, 'feed', limit] as const,
    byId: (id: string) => [...queryKeys.jobs.all, 'one', id] as const,
  },
  themes: {
    all: ['themes'] as const,
    list: () => [...queryKeys.themes.all, 'list'] as const,
  },
  uploads: {
    all: ['uploads'] as const,
    status: (channel: string) => [...queryKeys.uploads.all, 'status', channel] as const,
    youtubeVideos: (channel: string) =>
      [...queryKeys.uploads.all, 'youtube-videos', channel] as const,
    channelStats: (channel: string) =>
      [...queryKeys.uploads.all, 'channel-stats', channel] as const,
    statsSync: (channel: string) => [...queryKeys.uploads.all, 'stats-sync', channel] as const,
    tiktokStatus: (channel: string) =>
      [...queryKeys.uploads.all, 'tiktok-status', channel] as const,
  },
  scheduler: {
    all: ['scheduler'] as const,
    config: () => [...queryKeys.scheduler.all, 'config'] as const,
    state: () => [...queryKeys.scheduler.all, 'state'] as const,
    channels: () => [...queryKeys.scheduler.all, 'channels'] as const,
    channelConfig: (slug: string) => [...queryKeys.scheduler.all, 'channel-config', slug] as const,
    channelHistory: (slug: string, days: number) =>
      [...queryKeys.scheduler.all, 'channel-history', slug, days] as const,
    channelBacklog: (slug: string) =>
      [...queryKeys.scheduler.all, 'channel-backlog', slug] as const,
    resolvedPreset: (slug: string, factory: string) =>
      [...queryKeys.scheduler.all, 'resolved-preset', slug, factory] as const,
    globalEnabled: () => [...queryKeys.scheduler.all, 'global-enabled'] as const,
    studioThumbnails: () => [...queryKeys.scheduler.all, 'studio-thumbnails'] as const,
    factories: () => [...queryKeys.scheduler.all, 'factories'] as const,
    cronHealth: () => [...queryKeys.scheduler.all, 'cron-health'] as const,
    cortesSources: (slug: string) => [...queryKeys.scheduler.all, 'cortes-sources', slug] as const,
    retentionPreview: (before: string, channel: string | null) =>
      [...queryKeys.scheduler.all, 'retention-preview', before, channel ?? null] as const,
    /** Destinos do canal (Instagram…): config, conta e publicador. */
    destinations: (slug: string) => [...queryKeys.scheduler.all, 'destinations', slug] as const,
    /** Prefixo de todas as páginas da fila de um canal — para invalidar. */
    destinationPostsAll: (slug: string) => [...queryKeys.scheduler.all, 'destination-posts', slug] as const,
    destinationPosts: (slug: string, params: { limit: number; offset: number; statuses: string[] }) =>
      [...queryKeys.scheduler.all, 'destination-posts', slug, params] as const,
  },
  pools: {
    all: ['pools'] as const,
    list: () => [...queryKeys.pools.all, 'list'] as const,
    byId: (slug: string) => [...queryKeys.pools.all, 'one', slug] as const,
    linkPreview: (slug: string, channel: string) =>
      [...queryKeys.pools.all, 'link-preview', slug, channel] as const,
    deletePreview: (slug: string) => [...queryKeys.pools.all, 'delete-preview', slug] as const,
  },
  engagement: {
    all: ['engagement'] as const,
    channel: (slug: string) => [...queryKeys.engagement.all, 'channel', slug] as const,
    posts: (slug: string) => [...queryKeys.engagement.all, 'posts', slug] as const,
    session: () => [...queryKeys.engagement.all, 'session'] as const,
    summary: () => [...queryKeys.engagement.all, 'summary'] as const,
  },
  recovery: {
    all: ['recovery'] as const,
    status: () => [...queryKeys.recovery.all, 'status'] as const,
    batches: (slug: string) => [...queryKeys.recovery.all, 'batches', slug] as const,
    experiment: (slug: string) => [...queryKeys.recovery.all, 'experiment', slug] as const,
  },
  me: () => ['me'] as const,
  settings: {
    all: ['settings'] as const,
    global: () => [...queryKeys.settings.all, 'global'] as const,
    ollamaStatus: () => [...queryKeys.settings.all, 'ollama-status'] as const,
    studioCovers: () => [...queryKeys.settings.all, 'studio-covers'] as const,
    phoneLock: () => [...queryKeys.settings.all, 'phone-lock'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    catalog: () => [...queryKeys.notifications.all, 'catalog'] as const,
    settings: (channelSlug: string | null) =>
      [...queryKeys.notifications.all, 'settings', channelSlug ?? null] as const,
  },
  factoryPresets: {
    all: ['factory-presets'] as const,
    list: (factory: string) => [...queryKeys.factoryPresets.all, 'list', factory] as const,
  },
  schedules: {
    all: ['schedules'] as const,
    list: (channel: string) => [...queryKeys.schedules.all, 'list', channel] as const,
  },
  safety: {
    all: ['safety'] as const,
    verdict: (videoId: string, lang: string) =>
      [...queryKeys.safety.all, 'verdict', videoId, lang] as const,
    audit: (videoId: string, lang: string) =>
      [...queryKeys.safety.all, 'audit', videoId, lang] as const,
  },
  ranking: {
    all: ['ranking'] as const,
    byId: (videoId: string, lang: string) =>
      [...queryKeys.ranking.all, 'one', videoId, lang] as const,
    list: (channel: string, limit: number) =>
      [...queryKeys.ranking.all, 'list', channel, limit] as const,
    stage: (channel: string) => [...queryKeys.ranking.all, 'stage', channel] as const,
    calibrations: (channel: string, limit: number) =>
      [...queryKeys.ranking.all, 'calibrations', channel, limit] as const,
  },
  thumbnail: {
    all: ['thumbnail'] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    videoSnapshots: (videoId: string, lang: string) =>
      [...queryKeys.analytics.all, 'video-snapshots', videoId, lang] as const,
    videoYoutube: (videoId: string, lang: string) =>
      [...queryKeys.analytics.all, 'video-youtube', videoId, lang] as const,
    costs: (params: { days: number; channel: string | null; factory: string | null }) =>
      [...queryKeys.analytics.all, 'costs', params] as const,
    peakHours: (params: { channel: string; days: number; tz: string }) =>
      [...queryKeys.analytics.all, 'peak-hours', params] as const,
    correlation: (params: { channel: string; metric: string; days: number }) =>
      [...queryKeys.analytics.all, 'correlation', params] as const,
    retentionAudit: (params: { channel: string; limit: number; offset: number }) =>
      [...queryKeys.analytics.all, 'retention-audit', params] as const,
    compare: (params: { platform: string; days: number }) =>
      [...queryKeys.analytics.all, 'compare', params] as const,
  },
  media: {
    all: ['media'] as const,
    subtitleStyles: () => [...queryKeys.media.all, 'subtitle-styles'] as const,
    thumbnailStyles: () => [...queryKeys.media.all, 'thumbnail-styles'] as const,
    ttsVoices: (language: string, provider: string) =>
      [...queryKeys.media.all, 'tts-voices', language, provider] as const,
  },
  presentation: {
    all: ['presentation'] as const,
    history: (videoId: string, language: string) =>
      [...queryKeys.presentation.all, 'history', videoId, language] as const,
  },
  scraping: {
    all: ['scraping'] as const,
    cdpStatus: () => [...queryKeys.scraping.all, 'cdp-status'] as const,
  },
} as const
